// Phase 17 — Realtime notifications hook.
//
// Connects to `/api/realtime/stream` (SSE), keeps the unread DM count fresh,
// and dispatches a global `lifted:new-message` event whenever a new DM
// notification lands. Components that need to refresh on a new message
// (Inbox list, Messages page) can listen for that event.
//
// Falls back to a 10s GET /api/realtime/unread poll if EventSource is not
// available (very old browsers / SSR).

import { useEffect, useState, useCallback, useRef } from "react";
import api, { API_BASE } from "@/lib/api";

const EVT_NEW_MESSAGE = "lifted:new-message";
const EVT_UNREAD = "lifted:unread-changed";

export default function useRealtime(user) {
  const [unread, setUnread] = useState(0);
  const sourceRef = useRef(null);

  // Plain GET fallback — also seeds initial count
  const refreshUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/realtime/unread");
      setUnread(data?.unread ?? 0);
      window.dispatchEvent(new CustomEvent(EVT_UNREAD, { detail: data?.unread ?? 0 }));
    } catch (_) { /* noop */ }
  }, []);

  useEffect(() => {
    if (!user) {
      setUnread(0);
      return;
    }
    refreshUnread();

    // SSE wiring — the backend accepts the access token via ?access_token=
    // OR the standard Authorization header. EventSource can't send custom
    // headers, so we use the cookie-flavored path (`withCredentials`) which
    // sends the access_token cookie automatically.
    if (typeof window === "undefined" || !("EventSource" in window)) {
      const t = setInterval(refreshUnread, 10_000);
      return () => clearInterval(t);
    }

    const url = `${API_BASE}/realtime/stream`;
    let es;
    try {
      es = new EventSource(url, { withCredentials: true });
    } catch (_) {
      const t = setInterval(refreshUnread, 10_000);
      return () => clearInterval(t);
    }
    sourceRef.current = es;

    es.addEventListener("unread", (e) => {
      try {
        const d = JSON.parse(e.data);
        setUnread(d.unread ?? 0);
        window.dispatchEvent(new CustomEvent(EVT_UNREAD, { detail: d.unread ?? 0 }));
      } catch (_) {}
    });

    const onIncoming = (e) => {
      try {
        const d = JSON.parse(e.data);
        window.dispatchEvent(new CustomEvent(EVT_NEW_MESSAGE, { detail: d }));
      } catch (_) {}
    };
    es.addEventListener("message", onIncoming);
    es.addEventListener("dm", onIncoming);
    es.addEventListener("broadcast", onIncoming);
    es.addEventListener("affiliate_conversion", onIncoming);

    es.onerror = () => {
      // SSE will auto-reconnect — if it stays down, fall back to polling
      // once. Browser handles reconnection backoff for us.
      refreshUnread();
    };

    return () => {
      try { es.close(); } catch (_) {}
      sourceRef.current = null;
    };
  }, [user, refreshUnread]);

  // Optional manual decrement when user marks a thread read
  const decrementBy = useCallback((n = 1) => {
    setUnread((v) => Math.max(0, v - n));
  }, []);

  return { unread, refreshUnread, decrementBy };
}

export { EVT_NEW_MESSAGE, EVT_UNREAD };
